import type { ReactElement } from 'react';
import { useT } from '../lib/i18n';
import { Brand } from './Brand';
import { SearchTrigger } from './SearchTrigger';
import { SessionControls } from './SessionControls';

/**
 * The bar the application carries **below `lg`**, where there is no rail.
 *
 * On a wide screen `AppShell` shows the rail instead, and the same controls live
 * at its foot — hence `SessionControls`, so that neither copy can drift from the
 * other. Search and quick capture are the same story: the rail has its own pair,
 * and this is where a phone finds them — a phone being where a note gets written
 * in a hurry, and where there is no keyboard shortcut to reach either.
 *
 * The height is reserved rather than inferred from the content, so that nothing in
 * the bar moves between one page and the next.
 */
export function TopBar({
  onCapture,
  onSearch,
}: {
  onCapture: () => void;
  onSearch: () => void;
}): ReactElement {
  const t = useT();

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-paper/90 backdrop-blur-md lg:hidden">
      <div className="mx-auto flex min-h-16 max-w-3xl items-center gap-2 px-5 py-3 sm:px-8">
        <Brand className="min-w-0 flex-1" />
        <SearchTrigger onOpen={onSearch} className="size-9 shrink-0" />
        <button
          type="button"
          onClick={onCapture}
          aria-label={t('notes.capture')}
          className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent transition-colors hover:bg-accent hover:text-accent-ink"
        >
          <svg
            viewBox="0 0 24 24"
            className="size-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
        <SessionControls />
      </div>
    </header>
  );
}
